/**
 * "Now Playing" overlay — title / artist / album + optional timed lyrics.
 *
 * The overlay is rendered as a positioned `<div>` over the aspect frame for
 * live display. For recordings, the DOM layer is *not* visible to
 * `canvas.captureStream()` — the RecordingCompositor reads
 * `getRenderState()` each frame and re-draws the overlay via Canvas2D so it
 * lands in the saved file. The lyric ticker re-syncs each frame to the
 * audio element's `currentTime` so it stays in lock-step even when the
 * user scrubs the file player.
 */

export type OverlayPosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "center";

export type FontFamily =
  | "sans"
  | "grotesk"
  | "display"
  | "condensed"
  | "poster"
  | "serif"
  | "editorial"
  | "classic"
  | "mono"
  | "monoDisplay"
  | "sciFi"
  | "wide"
  | "neon"
  | "hand";

export type FontWeight = "light" | "regular" | "semibold" | "bold" | "black";

export type LyricMode = "single" | "karaoke";

export interface OverlayConfig {
  enabled: boolean;
  title: string;
  artist: string;
  album: string;
  position: OverlayPosition;
  /** Raw lyrics text — either LRC ([mm:ss.xx] line) or plain lines. */
  lyrics: string;
  /** Show the live lyric line under the song info. */
  showLyrics: boolean;
  /** Single active line, or 3-line karaoke stack (prev/now/next). */
  lyricMode: LyricMode;
  /** Optional album-art image (data URL). Empty string = none. */
  albumArt: string;
  // ---- Typography ----
  fontFamily: FontFamily;
  fontWeight: FontWeight;
  uppercase: boolean;
  // ---- Brand watermark (independent of song info) ----
  watermarkEnabled: boolean;
  /** Optional logo image (data URL). Empty = text-only watermark. */
  watermarkLogo: string;
  /** Handle text, e.g. "@yourname". */
  watermarkHandle: string;
  watermarkPosition: OverlayPosition;
}

export const DEFAULT_OVERLAY: OverlayConfig = {
  enabled: false,
  title: "",
  artist: "",
  album: "",
  position: "bottom-left",
  lyrics: "",
  showLyrics: true,
  lyricMode: "karaoke",
  albumArt: "",
  fontFamily: "sans",
  fontWeight: "bold",
  uppercase: false,
  watermarkEnabled: false,
  watermarkLogo: "",
  watermarkHandle: "",
  watermarkPosition: "bottom-right",
};

interface ParsedLyric {
  t: number; // seconds
  line: string;
}

/**
 * Flat snapshot of overlay state, consumed by RecordingCompositor for
 * Canvas2D rendering during recording / snapshot. Mirrors what's currently
 * visible in the DOM (post-tick lyric stack, post-applyConfig text fields).
 */
export interface NowPlayingRenderState {
  enabled: boolean;
  position: OverlayPosition;
  fontFamily: FontFamily;
  fontWeight: FontWeight;
  uppercase: boolean;
  title: string;
  artist: string;
  album: string;
  albumArt: string;
  lyricMode: LyricMode;
  showLyrics: boolean;
  lyricPrev: string;
  lyricNow: string;
  lyricNext: string;
  watermarkEnabled: boolean;
  watermarkLogo: string;
  watermarkHandle: string;
  watermarkPosition: OverlayPosition;
}

/**
 * Parses LRC-style lyrics. Lines without timestamps are treated as opening
 * "static" lines that show before the first stamped line. Multiple
 * timestamps on a single line (a common LRC pattern) are expanded.
 */
export function parseLyrics(raw: string): ParsedLyric[] {
  if (!raw.trim()) return [];
  const out: ParsedLyric[] = [];
  const stampRe = /\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\]/g;
  for (const lineRaw of raw.split(/\r?\n/)) {
    const stamps: number[] = [];
    let m: RegExpExecArray | null;
    stampRe.lastIndex = 0;
    while ((m = stampRe.exec(lineRaw)) !== null) {
      const min = Number(m[1]);
      const sec = Number(m[2]);
      const frac = m[3] ? Number(`0.${m[3]}`) : 0;
      stamps.push(min * 60 + sec + frac);
    }
    const text = lineRaw.replace(stampRe, "").trim();
    if (stamps.length === 0) {
      // Untimed lines only count if we have *no* timestamps yet — treat as a
      // single t=0 line to anchor the start of the song.
      if (out.length === 0 && text) out.push({ t: 0, line: text });
      continue;
    }
    for (const t of stamps) out.push({ t, line: text });
  }
  out.sort((a, b) => a.t - b.t);
  return out;
}

/**
 * Renders + manages the overlay DOM. Construction is idempotent — it expects
 * a host container (the `#frame` element) to mount into.
 */
export class NowPlayingOverlay {
  private root: HTMLDivElement;
  private artEl: HTMLImageElement;
  private textWrap: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private artistEl: HTMLDivElement;
  private albumEl: HTMLDivElement;
  /** 3-line karaoke stack: previous, active, upcoming. */
  private lyricStack: HTMLDivElement;
  private lyricPrev: HTMLDivElement;
  private lyricNow: HTMLDivElement;
  private lyricNext: HTMLDivElement;
  /** Brand watermark (independent of song info). */
  private watermark: HTMLDivElement;
  private watermarkLogo: HTMLImageElement;
  private watermarkHandle: HTMLDivElement;
  private parsedLyrics: ParsedLyric[] = [];
  private cfg: OverlayConfig = { ...DEFAULT_OVERLAY };
  private lastActiveLine = "";

  constructor(host: HTMLElement) {
    // ---- Now Playing block ----
    this.root = document.createElement("div");
    this.root.className = "nowPlaying";
    this.root.setAttribute("aria-hidden", "true");

    this.artEl = document.createElement("img");
    this.artEl.className = "nowPlaying__art";
    this.artEl.alt = "";

    this.textWrap = document.createElement("div");
    this.textWrap.className = "nowPlaying__text";

    this.titleEl = document.createElement("div");
    this.titleEl.className = "nowPlaying__title";
    this.artistEl = document.createElement("div");
    this.artistEl.className = "nowPlaying__artist";
    this.albumEl = document.createElement("div");
    this.albumEl.className = "nowPlaying__album";

    this.lyricStack = document.createElement("div");
    this.lyricStack.className = "nowPlaying__lyricStack";
    this.lyricPrev = document.createElement("div");
    this.lyricPrev.className = "nowPlaying__lyric nowPlaying__lyric--prev";
    this.lyricNow = document.createElement("div");
    this.lyricNow.className = "nowPlaying__lyric nowPlaying__lyric--now";
    this.lyricNext = document.createElement("div");
    this.lyricNext.className = "nowPlaying__lyric nowPlaying__lyric--next";
    this.lyricStack.append(this.lyricPrev, this.lyricNow, this.lyricNext);

    this.textWrap.append(
      this.titleEl,
      this.artistEl,
      this.albumEl,
      this.lyricStack,
    );
    this.root.append(this.artEl, this.textWrap);
    host.appendChild(this.root);

    // ---- Watermark block ----
    this.watermark = document.createElement("div");
    this.watermark.className = "watermark";
    this.watermark.setAttribute("aria-hidden", "true");
    this.watermarkLogo = document.createElement("img");
    this.watermarkLogo.className = "watermark__logo";
    this.watermarkLogo.alt = "";
    this.watermarkHandle = document.createElement("div");
    this.watermarkHandle.className = "watermark__handle";
    this.watermark.append(this.watermarkLogo, this.watermarkHandle);
    host.appendChild(this.watermark);

    this.applyConfig();
  }

  setConfig(next: OverlayConfig): void {
    this.cfg = { ...next };
    this.parsedLyrics = parseLyrics(this.cfg.lyrics);
    this.applyConfig();
  }

  getConfig(): OverlayConfig {
    return { ...this.cfg };
  }

  /**
   * Snapshot of the *currently visible* overlay state. Reads from the DOM so
   * the lyric stack reflects whatever the most recent `tick()` chose. Used
   * by RecordingCompositor to bake the overlay into recordings + snapshots.
   */
  getRenderState(): NowPlayingRenderState {
    const visibleText = (el: HTMLElement): string =>
      el.style.display === "none" ? "" : (el.textContent ?? "");
    return {
      enabled: this.cfg.enabled,
      position: this.cfg.position,
      fontFamily: this.cfg.fontFamily,
      fontWeight: this.cfg.fontWeight,
      uppercase: this.cfg.uppercase,
      title: this.cfg.title,
      artist: this.cfg.artist,
      album: this.cfg.album,
      albumArt: this.cfg.albumArt,
      lyricMode: this.cfg.lyricMode,
      showLyrics: this.cfg.showLyrics,
      lyricPrev: visibleText(this.lyricPrev),
      lyricNow: visibleText(this.lyricNow),
      lyricNext: visibleText(this.lyricNext),
      watermarkEnabled: this.cfg.watermarkEnabled,
      watermarkLogo: this.cfg.watermarkLogo,
      watermarkHandle: this.cfg.watermarkHandle,
      watermarkPosition: this.cfg.watermarkPosition,
    };
  }

  /** Per-frame tick — pass the song's current time in seconds (or `null`). */
  tick(currentTimeSec: number | null): void {
    if (
      !this.cfg.enabled ||
      !this.cfg.showLyrics ||
      this.parsedLyrics.length === 0
    ) {
      this.hideLyricStack();
      return;
    }
    const t = currentTimeSec ?? 0;
    const idx = this.findActiveIndex(t);
    const active = idx >= 0 ? this.parsedLyrics[idx] : null;
    const prev = idx > 0 ? this.parsedLyrics[idx - 1] : null;
    const next =
      idx >= 0 && idx < this.parsedLyrics.length - 1
        ? this.parsedLyrics[idx + 1]
        : idx < 0 && this.parsedLyrics.length > 0
          ? this.parsedLyrics[0]
          : null;
    const activeText = active?.line ?? "";
    const prevText = prev?.line ?? "";
    const nextText = next?.line ?? "";

    if (this.cfg.lyricMode === "single") {
      this.lyricPrev.style.display = "none";
      this.lyricNext.style.display = "none";
      this.lyricNow.textContent = activeText;
      this.lyricNow.style.display = activeText ? "" : "none";
      this.lyricStack.style.display =
        activeText || currentTimeSec === null ? "" : "none";
    } else {
      // Karaoke ticker (prev / now / next).
      this.lyricPrev.textContent = prevText;
      this.lyricNow.textContent = activeText;
      this.lyricNext.textContent = nextText;
      this.lyricPrev.style.display = prevText ? "" : "none";
      this.lyricNow.style.display = activeText ? "" : "none";
      this.lyricNext.style.display = nextText ? "" : "none";
      this.lyricStack.style.display =
        prevText || activeText || nextText ? "" : "none";
    }

    // Re-trigger the active-line fade-in animation when it changes.
    if (activeText && activeText !== this.lastActiveLine) {
      this.lyricNow.classList.remove("is-flash");
      // Force a reflow so the animation re-runs.
      void this.lyricNow.offsetWidth;
      this.lyricNow.classList.add("is-flash");
      this.lastActiveLine = activeText;
    } else if (!activeText) {
      this.lastActiveLine = "";
    }
  }

  private hideLyricStack(): void {
    this.lyricStack.style.display = "none";
    this.lastActiveLine = "";
  }

  /**
   * Best-effort: read ID3 tags from a music file by parsing the first 64 KB
   * of the file (covers ID3v2 at the start). Returns `null` for tag fields
   * the file doesn't have. Falls back to filename-derived title.
   */
  static async readMetadataFromFile(file: File): Promise<{
    title: string;
    artist: string;
    album: string;
  }> {
    const fallback = {
      title: file.name.replace(/\.[^.]+$/, ""),
      artist: "",
      album: "",
    };
    try {
      const head = new Uint8Array(await file.slice(0, 65536).arrayBuffer());
      const tags = readId3v2(head);
      return {
        title: tags.title || fallback.title,
        artist: tags.artist || "",
        album: tags.album || "",
      };
    } catch {
      return fallback;
    }
  }

  private applyConfig(): void {
    const c = this.cfg;

    // Now Playing block visibility + position.
    this.root.style.display = c.enabled ? "" : "none";
    this.root.dataset.position = c.position;

    // Typography (applied at the root so it cascades to title / artist / lyrics).
    this.root.dataset.font = c.fontFamily;
    this.root.dataset.weight = c.fontWeight;
    this.root.classList.toggle("is-uppercase", c.uppercase);

    // Album art.
    if (c.albumArt) {
      this.artEl.src = c.albumArt;
      this.artEl.style.display = "";
    } else {
      this.artEl.removeAttribute("src");
      this.artEl.style.display = "none";
    }

    // Text fields.
    this.titleEl.textContent = c.title;
    this.titleEl.style.display = c.title ? "" : "none";
    this.artistEl.textContent = c.artist;
    this.artistEl.style.display = c.artist ? "" : "none";
    this.albumEl.textContent = c.album;
    this.albumEl.style.display = c.album ? "" : "none";

    // Watermark.
    this.watermark.style.display = c.watermarkEnabled ? "" : "none";
    this.watermark.dataset.font = c.fontFamily;
    this.watermark.dataset.weight = c.fontWeight;
    this.watermark.classList.toggle("is-uppercase", c.uppercase);
    this.watermark.dataset.position = c.watermarkPosition;
    if (c.watermarkLogo) {
      this.watermarkLogo.src = c.watermarkLogo;
      this.watermarkLogo.style.display = "";
    } else {
      this.watermarkLogo.removeAttribute("src");
      this.watermarkLogo.style.display = "none";
    }
    this.watermarkHandle.textContent = c.watermarkHandle;
    this.watermarkHandle.style.display = c.watermarkHandle ? "" : "none";
  }

  private findActiveIndex(t: number): number {
    let idx = -1;
    for (let i = 0; i < this.parsedLyrics.length; i++) {
      if (this.parsedLyrics[i].t <= t) idx = i;
      else break;
    }
    return idx;
  }
}

// ---------------------------------------------------------------------------
// Minimal ID3v2 reader (text frames only)
// ---------------------------------------------------------------------------

interface Id3Tags {
  title?: string;
  artist?: string;
  album?: string;
}

function readId3v2(bytes: Uint8Array): Id3Tags {
  // ID3v2 header: "ID3" + version (2 bytes) + flags (1 byte) + size (4
  // synchsafe bytes). If the file doesn't start with "ID3", there's nothing
  // to read.
  if (
    bytes.length < 10 ||
    bytes[0] !== 0x49 ||
    bytes[1] !== 0x44 ||
    bytes[2] !== 0x33
  ) {
    return {};
  }
  const major = bytes[3];
  const tagSize = synchSafeInt(bytes[6], bytes[7], bytes[8], bytes[9]);
  const end = Math.min(bytes.length, 10 + tagSize);
  const tags: Id3Tags = {};
  let i = 10;
  while (i + 10 < end) {
    const frameId = String.fromCharCode(
      bytes[i],
      bytes[i + 1],
      bytes[i + 2],
      bytes[i + 3],
    );
    if (!/^[A-Z0-9]{4}$/.test(frameId)) break; // padding
    const frameSize =
      major >= 4
        ? synchSafeInt(bytes[i + 4], bytes[i + 5], bytes[i + 6], bytes[i + 7])
        : (bytes[i + 4] << 24) |
          (bytes[i + 5] << 16) |
          (bytes[i + 6] << 8) |
          bytes[i + 7];
    const dataStart = i + 10;
    const dataEnd = dataStart + frameSize;
    if (frameSize > 0 && dataEnd <= end) {
      const value = readTextFrame(bytes.subarray(dataStart, dataEnd));
      if (frameId === "TIT2" && value) tags.title = value;
      else if (frameId === "TPE1" && value) tags.artist = value;
      else if (frameId === "TALB" && value) tags.album = value;
    }
    i = dataEnd;
  }
  return tags;
}

function synchSafeInt(a: number, b: number, c: number, d: number): number {
  return ((a & 0x7f) << 21) | ((b & 0x7f) << 14) | ((c & 0x7f) << 7) | (d & 0x7f);
}

function readTextFrame(buf: Uint8Array): string {
  if (buf.length < 1) return "";
  const encoding = buf[0];
  const data = buf.subarray(1);
  // Strip null terminators ID3 likes to append.
  const trimEnd = (s: string): string => s.replace(/\0+$/g, "").trim();
  try {
    if (encoding === 0 || encoding === 3) {
      // 0 = ISO-8859-1, 3 = UTF-8 (close enough — TextDecoder defaults to utf-8)
      const dec = new TextDecoder(encoding === 0 ? "iso-8859-1" : "utf-8");
      return trimEnd(dec.decode(data));
    }
    if (encoding === 1 || encoding === 2) {
      // UTF-16 with optional BOM
      const dec = new TextDecoder("utf-16");
      return trimEnd(dec.decode(data));
    }
  } catch {
    /* fall through */
  }
  return "";
}
